<template>
  <router-view></router-view>
</template>

<script>
import { useAuthStore } from './stores/auth'
import { useAcampantesStore } from './stores/acampantes'
import pusher from './lib/pusher'

export default {
  name: 'App',
  data() {
    return { channel: null }
  },
  computed: {
    authStore() { return useAuthStore() },
    acampantes() { return useAcampantesStore() }
  },
  mounted() {
    if (this.$route.meta.public) return
    this.subscribePusher()
  },
  watch: {
    '$route.meta.public'(isPublic) {
      if (isPublic) this.unsubscribePusher()
      else this.subscribePusher()
    }
  },
  beforeUnmount() {
    this.unsubscribePusher()
  },
  methods: {
    subscribePusher() {
      if (!pusher || this.channel) return
      this.channel = pusher.subscribe('acamp-channel')

      this.channel.bind('new-user', res => {
        this.acampantes.addFromPusher(this.sanitizeFin(res.newUser))
        this.$toast.open({ message: res.message, type: 'info', position: 'top-right', duration: 4000 })
      })

      this.channel.bind('update-user', res => {
        this.acampantes.updateFromPusher(this.sanitizeFin(res.updatedUser))
        this.$toast.open({ message: res.message, type: 'info', position: 'top-right', duration: 3000 })
      })

      this.channel.bind('remove-user', res => {
        this.acampantes.removeFromPusher(res.deletedUser.id)
        this.$toast.open({ message: res.message, type: 'warning', position: 'top-right', duration: 4000 })
      })

      this.channel.bind('check-user', res => {
        this.acampantes.updateFromPusher(this.sanitizeFin(res.updatedUser))
        this.$toast.open({ message: res.message, type: 'success', position: 'top-right', duration: 4000 })
      })

      this.channel.bind('uncheck-user', res => {
        this.acampantes.updateFromPusher(this.sanitizeFin(res.updatedUser))
        this.$toast.open({ message: res.message, type: 'warning', position: 'top-right', duration: 4000 })
      })

      this.channel.bind('ficha-preenchida', res => {
        this.acampantes.markFichaPreenchida(res.id, res.fichaPreenchidaEm)
        this.$toast.open({
          message: `📋 ${res.name} enviou a ficha de inscrição`,
          type: 'info',
          position: 'top-right',
          duration: 4000
        })
      })
    },
    unsubscribePusher() {
      if (!pusher || !this.channel) return
      this.channel.unbind_all()
      pusher.unsubscribe('acamp-channel')
      this.channel = null
    },
    sanitizeFin(payload) {
      if (!payload || !this.authStore.isCheckin) return payload
      // Remove campos financeiros do payload Pusher pra CHECKIN
      const { valorTotal, valorPago, falta, pagamentos, ...rest } = payload
      return rest
    }
  }
}
</script>

<style>
:root {
  /* Base — neutros & tipografia */
  --color-text-primary: #2c3e50;
  --color-text-secondary: #666;
  --color-text-muted: #737373;
  --color-text-heading: #555;
  --color-text-dark: #333;
  --color-accent: #3366cc;
  --color-danger: #e74c3c;
  --color-success: #2ecc71;
  --color-warning: #f39c12;
  --color-bg-body: #eaeaea;
  --color-bg-surface: #ffffff;
  --color-bg-hover: #f5f5f5;
  --color-bg-separator: #f0f0f0;
  --modal-overlay: rgba(0, 0, 0, 0.86);
  --font-primary: Avenir, Helvetica, Arial, sans-serif;

  --text-xs: 12px;
  --text-sm: 14px;
  --text-base: 15px;
  --text-md: 16px;
  --text-lg: 18px;
  --text-xl: 26px;
  --text-2xl: 28px;
  --text-3xl: 32px;

  --spacing-xs: 2px;
  --spacing-sm: 5px;
  --spacing-md: 10px;
  --spacing-base: 20px;
  --spacing-lg: 30px;
  --spacing-xl: 40px;
  --spacing-2xl: 50px;

  --max-width: 1080px;
  --shadow-card: 0px 4px 9px rgba(0, 0, 0, 0.13);
  --shadow-hover: 0 10px 20px rgba(0, 0, 0, 0.2);
  --shadow-button: 0px 4px 10px rgba(0, 0, 0, 0.15);
  --shadow-button-hover: 0px 6px 15px rgba(0, 0, 0, 0.25);
  --radius-sm: 4px;
  --radius-md: 5px;
  --radius-lg: 10px;
  --radius-circle: 100%;
  --transition-default: all 0.3s ease-in-out;
  --transition-fast: all 0.2s ease-in-out;

  /* Tokens de equipe */
  --equipe-preta: #1a1a1a;
  --equipe-preta-text: #ffffff;
  --equipe-branca: #f5f5f5;
  --equipe-branca-text: #1a1a1a;
  --equipe-roxa: #7c3aed;
  --equipe-roxa-text: #ffffff;
  --equipe-verde: #10b981;
  --equipe-verde-text: #ffffff;

  /* Tokens de transporte */
  --transporte-proprio: #3366cc;
  --transporte-proprio-text: #ffffff;
  --transporte-onibus: #e67e22;
  --transporte-onibus-text: #ffffff;
}

* { margin: 0; padding: 0; box-sizing: border-box; touch-action: manipulation; }

body {
  background-color: var(--color-bg-body);
  font-family: var(--font-primary);
  color: var(--color-text-primary);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#app { min-height: 100vh; }

.btn-primary {
  display: inline-flex; align-items: center; gap: var(--spacing-sm);
  padding: 10px 20px; background-color: var(--color-accent); color: #fff;
  border: 2px solid var(--color-accent); border-radius: var(--radius-md);
  font-size: var(--text-sm); font-weight: 600; cursor: pointer;
  transition: var(--transition-default); box-shadow: var(--shadow-button);
  text-decoration: none; text-align: center;
}
.btn-primary:hover { background-color: #2a54a3; border-color: #2a54a3; box-shadow: var(--shadow-button-hover); }
.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-secondary {
  display: inline-flex; align-items: center; gap: var(--spacing-sm);
  padding: 10px 20px; background-color: var(--color-bg-surface); color: var(--color-accent);
  border: 2px solid var(--color-accent); border-radius: var(--radius-md);
  font-size: var(--text-sm); font-weight: 600; cursor: pointer;
  transition: var(--transition-default); box-shadow: var(--shadow-button);
  text-decoration: none; text-align: center;
}
.btn-secondary:hover { background-color: var(--color-accent); color: #fff; box-shadow: var(--shadow-button-hover); }

.btn-danger {
  display: inline-flex; align-items: center; gap: var(--spacing-sm);
  padding: 10px 20px; background-color: var(--color-danger); color: #fff;
  border: 2px solid var(--color-danger); border-radius: var(--radius-md);
  font-size: var(--text-sm); font-weight: 600; cursor: pointer;
  transition: var(--transition-default); box-shadow: var(--shadow-button);
}
.btn-danger:hover { background-color: #c0392b; border-color: #c0392b; box-shadow: var(--shadow-button-hover); }

.card {
  background-color: var(--color-bg-surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
}

.input {
  width: 100%; padding: 10px 14px;
  border: 1px solid #ddd; border-radius: var(--radius-md);
  font-size: var(--text-sm); font-family: var(--font-primary);
  color: var(--color-text-primary); background: var(--color-bg-surface);
  transition: var(--transition-fast); outline: none;
  -webkit-appearance: none;
  appearance: none;
}
.input:focus { border-color: var(--color-accent); box-shadow: 0 0 0 3px rgba(51, 102, 204, 0.15); }
.input::placeholder { color: var(--color-text-muted); }
/* iOS Safari faz zoom em inputs com font-size < 16px. Em telas mobile, forcamos 16px. */
@media (max-width: 768px) {
  .input { font-size: 16px; }
}

.label {
  display: block; font-size: var(--text-xs); font-weight: 600;
  color: var(--color-text-heading); margin-bottom: var(--spacing-sm);
  text-transform: uppercase; letter-spacing: 0.3px;
}

.error-msg { color: var(--color-danger); font-size: var(--text-xs); margin-top: var(--spacing-xs); }

.page-title {
  font-size: var(--text-xl); font-weight: 700;
  color: var(--color-text-primary); margin-bottom: var(--spacing-lg);
}

.modal-overlay {
  position: fixed; inset: 0; background: var(--modal-overlay);
  display: flex; align-items: center; justify-content: center; z-index: 100;
}
.modal-card {
  background: var(--color-bg-surface); border-radius: var(--radius-lg);
  padding: var(--spacing-lg); width: 90%; max-width: 500px;
  max-height: 90vh; overflow-y: auto; box-shadow: var(--shadow-hover);
  color: var(--color-text-primary);
}
.modal-title {
  font-size: var(--text-lg); font-weight: 700;
  margin-bottom: var(--spacing-base); color: var(--color-text-primary);
}
.modal-actions {
  display: flex; gap: var(--spacing-md);
  margin-top: var(--spacing-base); justify-content: flex-end;
}
</style>
